import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { CandidateStatusBar, WifiIcon } from './CandidateStatusBar';
import { candidateMark } from '../lib/branding';

/**
 * The window a candidate sits in.
 *
 * Deliberately not the console the staff use. On the day a candidate meets a
 * single full-width screen with a thin bar across the top and nothing else
 * competing for attention — no navigation down the side inviting them to
 * wander off mid-paper. Practising inside a different frame teaches the wrong
 * reflexes, which is the same reason the sections and the entry screen were
 * rebuilt to match.
 *
 * What a candidate still genuinely needs — their results, their password —
 * lives behind the menu rather than being removed, because the alternative is
 * a candidate who cannot change a password somebody else chose for them.
 */
/** The bell the test centre screen carries. Nothing rings; it is furniture. */
function BellIcon() {
  return (
    <svg viewBox="0 0 18 18" width="17" height="17" aria-hidden="true" focusable="false">
      <path
        d="M9 2.2a4.4 4.4 0 0 0-4.4 4.4v3L3.3 12.3h11.4L13.4 9.6v-3A4.4 4.4 0 0 0 9 2.2Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <path d="M7.4 14.2a1.7 1.7 0 0 0 3.2 0" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

export function CandidateFrame() {
  const { user, organization, signOut } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine);
  const menu = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  // A menu left hanging open over the paper is a distraction; it closes on the
  // next click anywhere else, as every menu should.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!menu.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  if (!user) return null;
  const mark = candidateMark(organization);
  const leave = () => void signOut().then(() => navigate('/login'));

  return (
    <div className="candidate">
      <header className="candidate__bar">
        {/* The centre's mark, and nothing where the account is the platform's
            own workspace rather than a centre — see candidateMark. */}
        <div className="candidate__brand">
          {mark &&
            ('logoUrl' in mark ? (
              <img className="candidate__logo" src={mark.logoUrl} alt={mark.alt} />
            ) : (
              <span className="candidate__wordmark">{mark.name}</span>
            ))}
        </div>

        {/* The candidate's number sits beside the centre's mark, as the test
            centre screen prints it. Their name is not repeated: they know it,
            and the number is what an invigilator asks for. */}
        {user.candidateNumber && (
          <span className="candidate__number">{user.candidateNumber}</span>
        )}

        <div className="candidate__controls" ref={menu}>
          <span className="candidate__gauge" title="Connected">
            <WifiIcon on={online} />
          </span>
          <span className="candidate__gauge" title="Notifications">
            <BellIcon />
          </span>
          <button
            type="button"
            className="candidate__iconbtn"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Menu"
            onClick={() => setMenuOpen((v) => !v)}
          >
            ☰
          </button>

          {menuOpen && (
            <div className="candidate__menu" role="menu">
              <NavLink to="/my/tests" role="menuitem" onClick={() => setMenuOpen(false)}>
                My tests
              </NavLink>
              <NavLink to="/my/results" role="menuitem" onClick={() => setMenuOpen(false)}>
                My results
              </NavLink>
              <NavLink to="/account" role="menuitem" onClick={() => setMenuOpen(false)}>
                Account
              </NavLink>
              {/* What a score here is and is not. The candidate reading a band
                  on this platform is the person with most to lose by mistaking
                  it for an examination result. */}
              <NavLink to="/legal" role="menuitem" onClick={() => setMenuOpen(false)}>
                About &amp; legal
              </NavLink>
            </div>
          )}

        </div>
      </header>

      <main className="candidate__main">
        <Outlet />
      </main>

      <CandidateStatusBar centre={mark && 'name' in mark ? mark.name : null} onExit={leave} />
    </div>
  );
}
